import { useRuntimeConfig } from '#imports'
import type { RouteLocationRaw } from 'vue-router'

import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { tw } from '@backmarket/utils/string/tw'
import { IconBackMarketPromise } from '@ds/icons/IconBackMarketPromise'
import { IconSwap } from '@ds/icons/IconSwap'

import {
  ABOUT_US,
  ABOUT_US_EXTERNAL_PAGE_VERIFIED_REFURBISHED,
} from '~/scopes/about-us/routes.constants'
import { CMS } from '~/scopes/cms/routes-names'

import translations from './useNavLinks.translations'

interface Options {
  withTradein: boolean
  variant?: 'small'
}

export function useNavLinks(options: Options = { withTradein: true }) {
  const i18n = useI18n()
  const locale = useI18nLocale()

  const {
    market: { countryCode },

    features,
  } = useMarketplace()
  const experiments = useExperiments()
  const config = useRuntimeConfig()

  const linksConfig = [
    {
      condition: () =>
        options.withTradein && features?.buyback?.headerButtonEnabled,
      name: 'buyback',
      href: {
        name: CMS.BUYBACK,
        params: { locale, pageName: 'home' },
      },
      getLabel: () => i18n(translations.sellYourTech),
      classNames: tw`text-static-brand-mid`,
      icon: IconSwap,
      url: `/${locale}/buyback/home`,
    },
    {
      condition: () =>
        config.public.FF_ENABLE_VERIFIED_REFURBISHED_LINK_HEADER?.includes(
          countryCode,
        ),
      name: 'footer_verified_refurbished',
      href: {
        name: ABOUT_US_EXTERNAL_PAGE_VERIFIED_REFURBISHED,
        params: { locale },
      },
      getLabel: () => i18n(translations.verifiedRefurbished),
      icon: options.variant === 'small' ? IconBackMarketPromise : undefined,
      url: `/${locale}/quality`,
    },
    {
      condition: () =>
        !config.public.FF_ENABLE_VERIFIED_REFURBISHED_LINK_HEADER?.includes(
          countryCode,
        ),
      name: 'footer_about_us',
      href: { name: ABOUT_US, params: { locale } },
      url: `/${locale}/about-us`,
      getLabel: () => i18n(translations.aboutUs),
    },
    {
      condition: () =>
        config.public.FF_ENABLE_BACK_STORIES_LINK_HEADER?.includes(countryCode),
      name: 'footer_back_stories',
      href: { name: CMS.BACK_STORIES, params: { locale, pageName: 'home' } },
      getLabel: () => i18n(translations.backStories),
      url: `/${locale}/back-stories/home`,
    },
    {
      condition: () =>
        config.public.FF_ENABLE_REPAIR_AND_CARE_LINK?.includes(countryCode) &&
        experiments['experiment.repairCTAInHeader'] !== 'withRepairCTAInHeader',
      name: 'header_to_repair_and_care',
      href: { name: CMS.REPAIR_AND_CARE, params: { locale } },
      getLabel: () => i18n(translations.repairAndCare),
      url: `/${locale}/repair-and-care`,
    },
    {
      condition: () =>
        config.public.FF_ENABLE_END_FAST_TECH_LINK?.includes(countryCode),
      name: 'header_to_end_fast_tech',
      href: { name: CMS.END_FAST_TECH, params: { locale } },
      url: `/${locale}/end-fast-tech`,
      getLabel: () => i18n(translations.endFastTech),
    },
    {
      condition: () =>
        config.public.FF_ENABLE_TECH_JOURNAL_LINK?.includes(countryCode),
      name: 'header_to_tech_journal',
      href: { name: CMS.ARTICLE_ROOT_HUB, params: { locale } },
      url: `/${locale}/c`,
      getLabel: () => i18n(translations.techJournal),
    },
  ]

  const links: Array<{
    name: string
    href: RouteLocationRaw
    label: string
    url?: string
    icon?: object
    classNames?: string
  }> = linksConfig
    .filter((item) => item.condition())
    .map(({ condition, getLabel, ...item }) => ({ ...item, label: getLabel() }))

  return { links }
}
